import type { 
  BreakpointKey, 
  BreakpointColumnConfig, 
  BreakpointRowConfig,
  BreakpointWidthConfig,
} from "../types";
import { BREAKPOINTS } from "../types";
import { 
  DEFAULT_BREAKPOINT_WIDTH, 
  createDefaultColumnConfig,
  createDefaultRowConfig,
} from "./tableConstants";

/**
 * Border side mapping for compact format
 */
const BORDER_SIDE_MAP: Record<string, string> = {
  t: 'top',
  r: 'right',
  b: 'bottom',
  l: 'left',
};

/**
 * Alignment CSS mapping
 */
const ALIGNMENT_CSS_MAP: Record<string, string> = {
  center: "margin: 0 auto; ",
  right: "margin-left: auto; margin-right: 0; ",
  left: "",
};

/**
 * Generate border CSS rules from compact format
 */
const generateCompactBorderCSS = (bd: string): string => {
  let rules = 'border-style:solid!important;';
  bd.split(';').forEach((side) => {
    const [pos, color, width] = side.split(':');
    const sideFullName = BORDER_SIDE_MAP[pos];
    if (sideFullName && color) {
      rules += `border-${sideFullName}-color:${color}!important;`;
      if (width) rules += `border-${sideFullName}-width:${width}px!important;`;
    }
  });
  return rules;
};

/**
 * Generate border CSS rules from full format
 */
const BORDER_SIDES = ['top', 'right', 'bottom', 'left'] as const;
const generateFullBorderCSS = (border: { top?: { color?: string; width?: string }; right?: { color?: string; width?: string }; bottom?: { color?: string; width?: string }; left?: { color?: string; width?: string } }): string => {
  let rules = 'border-style:solid!important;';
  BORDER_SIDES.forEach(side => {
    if (border[side]?.color) rules += `border-${side}-color:${border[side]!.color}!important;`;
    if (border[side]?.width) rules += `border-${side}-width:${border[side]!.width}px!important;`;
  });
  return rules;
};

/**
 * Generate border CSS for row styles (handles default comparisons)
 */
const DEFAULT_BORDER_COLOR = '#e5e7eb';
const DEFAULT_BORDER_WIDTH = '1';

interface BorderSide {
  color?: string;
  width?: string;
}

const shouldApplyBorder = (side: BorderSide | undefined): boolean => {
  if (!side) return false;
  return Boolean(
    (side.width && side.width !== DEFAULT_BORDER_WIDTH) || 
    (side.color && side.color !== DEFAULT_BORDER_COLOR)
  );
};

const generateRowBorderCSS = (border: { top?: BorderSide; right?: BorderSide; bottom?: BorderSide; left?: BorderSide }): string => {
  let cellRules = '';
  
  BORDER_SIDES.forEach(side => {
    const borderSide = border[side];
    if (shouldApplyBorder(borderSide)) {
      const color = borderSide?.color || DEFAULT_BORDER_COLOR;
      const width = borderSide?.width || DEFAULT_BORDER_WIDTH;
      cellRules += `border-${side}: ${width}px solid ${color} !important; `;
    }
  });
  
  return cellRules;
};

/**
 * Generate CSS for column styles (legacy index-based approach)
 */
export const generateColumnCSS = (
  colIndex: number, 
  styles: Record<string, Partial<BreakpointColumnConfig>>,
  tableId?: string
): string => {
  let css = "";
  const t = tableId ? `table[data-table-id="${tableId}"]` : "table";
  const defaults = createDefaultColumnConfig();
  
  (Object.keys(BREAKPOINTS) as BreakpointKey[]).forEach((key) => {
    const bp = BREAKPOINTS[key];
    const config = styles[key];
    if (!config) return;
    
    // Merge with defaults
    const widthType = config.widthType !== undefined ? config.widthType : defaults.widthType;
    const widthValue = config.widthValue !== undefined ? config.widthValue : defaults.widthValue;
    
    if (!widthValue) return;
    
    const w = widthType === "percent" ? `${widthValue}%` : `${widthValue}px`;
    const mq = bp.max !== Infinity ? `@media(min-width:${bp.min}px)and(max-width:${bp.max}px)` : `@media(min-width:${bp.min}px)`;
    css += `${mq}{${t} td[data-column-index="${colIndex}"],${t} th[data-column-index="${colIndex}"]{width:${w}!important;min-width:${w}!important;max-width:${w}!important}}`;
  });
  
  return css;
};

/**
 * Generate CSS for column styles using class-based approach
 */
export const generateColumnClassCSS = (
  columnClassName: string,
  styles: Record<string, Record<string, unknown>>,
  tableId?: string
): string => {
  let css = "";
  const t = tableId ? `table[data-table-id="${tableId}"]` : "table";
  const selector = `${t} td.${columnClassName},${t} th.${columnClassName}`;
  const defaults = createDefaultColumnConfig();
  
  // Check if only xs has data (for optimization)
  const otherBreakpoints = ['sm', 'md', 'lg', 'xl'] as const;
  const hasOtherBreakpointData = otherBreakpoints.some(bp => {
    const config = styles[bp];
    if (!config) return false;
    return config.wt || config.widthType || config.wv || config.widthValue;
  });
  
  // Build per-breakpoint CSS
  (Object.keys(BREAKPOINTS) as BreakpointKey[]).forEach((key) => {
    const bp = BREAKPOINTS[key];
    const config = styles[key] || {};
    
    // Handle compact keys (wt, wv) and full keys (widthType, widthValue)
    const widthType = (config.wt || config.widthType || defaults.widthType) as string;
    const widthValue = (config.wv || config.widthValue || defaults.widthValue) as string;
    
    if (!widthValue) return;
    
    const w = widthType === "percent" ? `${widthValue}%` : `${widthValue}px`;
    let rules = `width:${w}!important;min-width:${w}!important;max-width:${w}!important;`;
    
    const mq = bp.max !== Infinity ? `@media (min-width: ${bp.min}px) and (max-width: ${bp.max}px)` : `@media (min-width: ${bp.min}px)`;
    const formattedRules = rules.replace(/;/g, ';\n        ').slice(0, -9);
    css += `\n${mq} {\n    ${selector} {\n        ${formattedRules}\n    }\n}\n`;
  });
  
  return css;
};

/**
 * Generate CSS for column styles using column index (fallback when no class)
 */
export const generateColumnIndexCSS = (
  columnIndex: number,
  styles: Record<string, Record<string, unknown>>,
  tableId?: string
): string => {
  let css = "";
  const t = tableId ? `table[data-table-id="${tableId}"]` : "table";
  const selector = `${t} td[data-column-index="${columnIndex}"],${t} th[data-column-index="${columnIndex}"]`;
  const defaults = createDefaultColumnConfig();
  
  // Build per-breakpoint CSS
  (Object.keys(BREAKPOINTS) as BreakpointKey[]).forEach((key) => {
    const bp = BREAKPOINTS[key];
    const config = styles[key] || {};
    
    // Handle compact keys (wt, wv) and full keys (widthType, widthValue)
    const widthType = (config.wt || config.widthType || defaults.widthType) as string;
    const widthValue = (config.wv || config.widthValue || defaults.widthValue) as string;
    
    if (!widthValue) return;
    
    const w = widthType === "percent" ? `${widthValue}%` : `${widthValue}px`;
    const rules = `width:${w}!important;min-width:${w}!important;max-width:${w}!important;`;
    
    const mq = bp.max !== Infinity ? `@media (min-width: ${bp.min}px) and (max-width: ${bp.max}px)` : `@media (min-width: ${bp.min}px)`;
    const formattedRules = rules.replace(/;/g, ';\n        ').slice(0, -9);
    css += `\n${mq} {\n    ${selector} {\n        ${formattedRules}\n    }\n}\n`;
  });
  
  return css;
};

/**
 * Generate CSS for table styles using class-based approach
 */
export const generateTableClassCSS = (
  tableClassName: string,
  styles: Record<string, unknown>,
  tableId?: string
): string => {
  const t = tableId ? `table[data-table-id="${tableId}"]` : "table";
  
  // Extract table-level settings (handle both compact and full keys)
  const borderRadius = ((styles.tbr ?? styles.tableBorderRadius) || 0) as number;
  const borderColor = ((styles.tbc ?? styles.tableBorderColor) || "#000000") as string;
  const borderWidth = ((styles.tbw ?? styles.tableBorderWidth) ?? 1) as number;
  const hasRadius = borderRadius > 0;
  
  // Get table widths
  const tableWidths = ((styles.tw ?? styles.tableWidths) || {}) as Record<string, Record<string, unknown>>;
  const xsConfig = tableWidths.xs || {};
  const baseRowGap = ((xsConfig.rg ?? xsConfig.rowGap) || 0) as number;
  
  // Base table CSS
  let css = `
${t}.${tableClassName} {
    border-collapse: separate !important;
    border-spacing: 0 ${baseRowGap}px !important;
    border: none !important;
    ${hasRadius ? `border-radius: ${borderRadius}px !important;
    overflow: hidden !important;` : `border-radius: 0 !important;`}
}
`;

  // Corner border-radius styles
  if (hasRadius) {
    css += `
${t}.${tableClassName} tr:first-child th:first-child,
${t}.${tableClassName} tr:first-child td:first-child {
    border-top-left-radius: ${borderRadius}px !important;
}
${t}.${tableClassName} tr:first-child th:last-child,
${t}.${tableClassName} tr:first-child td:last-child {
    border-top-right-radius: ${borderRadius}px !important;
}
${t}.${tableClassName} tr:last-child td:first-child,
${t}.${tableClassName} tr:last-child th:first-child {
    border-bottom-left-radius: ${borderRadius}px !important;
}
${t}.${tableClassName} tr:last-child td:last-child,
${t}.${tableClassName} tr:last-child th:last-child {
    border-bottom-right-radius: ${borderRadius}px !important;
}
`;
  }
  
  // Border collapse fix.
  // The colour and width come from the table's own settings so that changing
  // them repaints every plain cell, including ones whose inline styles were
  // written before the change.
  css += `
${t}.${tableClassName} td:not([data-cell-class]):not([data-cell-id]),
${t}.${tableClassName} th:not([data-cell-class]):not([data-cell-id]) {
    border-style: solid;
    border-color: ${borderColor};
    border-width: ${borderWidth}px;
}
${t}.${tableClassName} td:not(:first-child):not([data-cell-class]):not([data-cell-id]),
${t}.${tableClassName} th:not(:first-child):not([data-cell-class]):not([data-cell-id]) {
    border-left-width: 0;
}
${t}.${tableClassName} tr:not(:first-child) td:not([data-cell-class]):not([data-cell-id]),
${t}.${tableClassName} tr:not(:first-child) th:not([data-cell-class]):not([data-cell-id]) {
    border-top-width: ${baseRowGap > 0 ? `${borderWidth}px` : "0"};
}
`;

  // Generate responsive CSS for widths
  if (Object.keys(tableWidths).length > 0) {
    Object.entries(BREAKPOINTS).forEach(([key, bp]) => {
      const widthConfig = tableWidths[key] as Record<string, unknown> | undefined;
      if (!widthConfig) return;
      
      const type = (widthConfig.t || widthConfig.type || "percent") as string;
      const value = (widthConfig.v || widthConfig.value || "") as string;
      const alignment = (widthConfig.a || widthConfig.alignment || "left") as string;
      const horizontalScroll = widthConfig.hs ?? widthConfig.horizontalScroll ?? false;
      const rowGap = widthConfig.rg ?? widthConfig.rowGap;
      
      let tableStyles = "";
      let hasCustomSettings = false;
      
      if (value) {
        const w = type === "percent" ? `${value}%` : `${value}px`;
        tableStyles += `width: ${w} !important; min-width: ${w} !important; max-width: ${w} !important; table-layout: fixed !important; `;
        hasCustomSettings = true;
      }
      
      if (rowGap !== undefined) {
        tableStyles += `border-spacing: 0 ${rowGap}px !important; `;
        hasCustomSettings = true;
      }
      
      // Apply alignment CSS using mapping
      const alignmentCSS = ALIGNMENT_CSS_MAP[alignment] || ALIGNMENT_CSS_MAP.left;
      if (alignmentCSS) {
        tableStyles += alignmentCSS;
        hasCustomSettings = Boolean(alignmentCSS.trim());
      }
      
      if (hasCustomSettings || horizontalScroll) {
        const mq = bp.max !== Infinity 
          ? `@media (min-width: ${bp.min}px) and (max-width: ${bp.max}px)` 
          : `@media (min-width: ${bp.min}px)`;
        
        let mqContent = `    ${t}.${tableClassName} {
        ${tableStyles.trim()}
    }`;
        
        // Add wrapper styles for horizontal scroll
        if (horizontalScroll && tableId) {
          mqContent += `
    .table-wrapper[data-for-table="${tableId}"] {
        overflow-x: auto !important;
        -webkit-overflow-scrolling: touch;
        width: 100%;
        display: block;
    }`;
        }
        
        css += `
${mq} {
${mqContent}
}
`;
      }
    });
  }
  
  return css;
};

/**
 * Generate complete table CSS including border radius and border collapse styles
 */
export const generateTableCSS = (
  tableId: string,
  tableSettings: {
    tableWidths?: Record<string, Partial<BreakpointWidthConfig>>;
    tableBorderColor?: string;
    tableBorderWidth?: number;
    tableBorderRadius?: number;
    tableRowGap?: number;
  }
): string => {
  const hasRadius = (tableSettings.tableBorderRadius || 0) > 0;
  const borderColor = tableSettings.tableBorderColor || "#000000";
  const borderWidth = tableSettings.tableBorderWidth !== undefined ? tableSettings.tableBorderWidth : 1;
  const borderRadius = tableSettings.tableBorderRadius || 0;
  // Get the smallest breakpoint's row gap for base CSS (fallback)
  const baseRowGap = tableSettings.tableWidths?.xs?.rowGap !== undefined ? tableSettings.tableWidths.xs.rowGap : (tableSettings.tableRowGap || 0);
  
  // Base table CSS for border styles
  // Always use cell borders only (no table border) to prevent double borders
  let baseTableCSS = `
table[data-table-id="${tableId}"] {
  border-collapse: separate !important;
  border-spacing: 0 ${baseRowGap}px !important;
  border: none !important;
  ${hasRadius ? `
  border-radius: ${borderRadius}px !important;
  overflow: hidden !important;
  ` : `
  border-radius: 0 !important;
  `}
}`;

  // Use shorter alias for table selector
  const t = `table[data-table-id="${tableId}"]`;
  
  // Add corner cell border-radius styles when radius is set (compact)
  if (hasRadius) {
    baseTableCSS += `
${t} tr:first-child th:first-child,${t} tr:first-child td:first-child{border-top-left-radius:${borderRadius}px!important}
${t} tr:first-child th:last-child,${t} tr:first-child td:last-child{border-top-right-radius:${borderRadius}px!important}
${t} tr:last-child td:first-child,${t} tr:last-child th:first-child{border-bottom-left-radius:${borderRadius}px!important}
${t} tr:last-child td:last-child,${t} tr:last-child th:last-child{border-bottom-right-radius:${borderRadius}px!important}`;
  }
  
  // Add border collapse fix (compact)
  baseTableCSS += `
${t} td:not([data-cell-id]):not([data-cell-class]),${t} th:not([data-cell-id]):not([data-cell-class]){border-style:solid}
${t} td:not(:first-child):not([data-cell-id]):not([data-cell-class]),${t} th:not(:first-child):not([data-cell-id]):not([data-cell-class]){border-left-width:0}
${t} tr:not(:first-child) td:not([data-cell-id]):not([data-cell-class]),${t} tr:not(:first-child) th:not([data-cell-id]):not([data-cell-class]){border-top-width:0}`;

  // Generate responsive CSS for widths - only for breakpoints with custom settings
  let responsiveCSS = baseTableCSS;
  
  if (tableSettings.tableWidths && Object.keys(tableSettings.tableWidths).length > 0) {
    Object.entries(BREAKPOINTS).forEach(([key, bp]) => {
      const widthConfig = tableSettings.tableWidths?.[key];
      if (!widthConfig) return;
      
      // Merge with defaults
      const defaults = DEFAULT_BREAKPOINT_WIDTH;
      const type = widthConfig.type !== undefined ? widthConfig.type : defaults.type;
      const value = widthConfig.value !== undefined ? widthConfig.value : defaults.value;
      const alignment = widthConfig.alignment !== undefined ? widthConfig.alignment : defaults.alignment;
      const horizontalScroll = widthConfig.horizontalScroll !== undefined ? widthConfig.horizontalScroll : defaults.horizontalScroll;
      const rowGap = widthConfig.rowGap !== undefined ? widthConfig.rowGap : undefined;
      
      let ts = "";
      let hasCustomWidth = false;
      let hasCustomAlignment = false;
      // rowGap is custom if it's explicitly set (even if 0, to override xs)
      let hasCustomRowGap = widthConfig.rowGap !== undefined;
      
      if (value) {
        const w = type === "percent" ? `${value}%` : `${value}px`;
        ts = `width:${w}!important;min-width:${w}!important;max-width:${w}!important;table-layout:fixed!important;`;
        hasCustomWidth = true;
      }
      
      // Add row gap to CSS if explicitly set (including 0 to override other breakpoints)
      if (hasCustomRowGap && rowGap !== undefined) {
        ts += `border-spacing:0 ${rowGap}px!important;`;
      }
      
      // Apply alignment CSS using mapping
      const alignmentCSS = ALIGNMENT_CSS_MAP[alignment];
      if (alignmentCSS) {
        ts += alignmentCSS.trim();
        hasCustomAlignment = true;
      }
      
      // Only generate media query if there's something custom to apply
      if (hasCustomWidth || hasCustomAlignment || hasCustomRowGap || horizontalScroll) {
        const mq = bp.max !== Infinity ? `@media(min-width:${bp.min}px)and(max-width:${bp.max}px)` : `@media(min-width:${bp.min}px)`;
        let mediaQueryContent = `${t}{${ts}}`;
        
        // Add wrapper styles for horizontal scroll
        if (horizontalScroll) {
          mediaQueryContent += `.table-wrapper[data-for-table="${tableId}"]{overflow-x:auto!important;-webkit-overflow-scrolling:touch;width:100%;display:block;}`;
        }
        
        responsiveCSS += `${mq}{${mediaQueryContent}}`;
      }
    });
  }
  
  return responsiveCSS;
};

/**
 * Generate CSS for cell styles using class-based approach
 */
export const generateCellClassCSS = (
  cellClassName: string,
  styles: Record<string, Record<string, unknown>>,
  tableId?: string
): string => {
  let css = "";
  const t = tableId ? `table[data-table-id="${tableId}"]` : "table";
  const selector = `${t} td.${cellClassName},${t} th.${cellClassName}`;
  
  // Get xs config (the base)
  const xsConfig = styles.xs || {};
  const globalBg = xsConfig.bg || xsConfig.backgroundColor;
  const globalColor = xsConfig.c || xsConfig.textColor;
  
  // Check if only xs has non-color data (padding, borders, etc.)
  const otherBreakpoints = ['sm', 'md', 'lg', 'xl'] as const;
  const hasOtherBreakpointData = otherBreakpoints.some(bp => {
    const config = styles[bp];
    if (!config) return false;
    // Check if this breakpoint has any non-color properties
    return config.p || config.padding || config.bd || config.border || 
           config.ta || config.textAlign || config.va || config.verticalAlign ||
           config.fs || config.fontSize || config.fw || config.fontWeight;
  });
  
  // Build global rules (properties that don't need media queries)
  let globalRules = "";
  if (globalBg) globalRules += `background-color:${globalBg}!important;`;
  if (globalColor) globalRules += `color:${globalColor}!important;`;
  
  // If only xs has data, we still need media queries for borders/padding
  // to prevent them from applying to all breakpoints
  // Only truly global properties (colors) go in globalRules
  if (!hasOtherBreakpointData) {
    // DON'T add padding/borders globally - they should be breakpoint-specific
    // Only add text align/vertical align/font properties if they're truly universal
    // For now, we'll treat all non-color properties as breakpoint-specific
  }
  
  if (globalRules) {
    // Format global rules with line breaks for readability
    const formattedGlobalRules = globalRules.replace(/;/g, ';\n    ').slice(0, -5); // Remove trailing newline+spaces
    css += `\n${selector} {\n    ${formattedGlobalRules}\n}\n`;
  }
  
  // Always output per-breakpoint CSS (even if only xs has data)
  // This ensures borders/padding are breakpoint-specific
  (Object.keys(BREAKPOINTS) as BreakpointKey[]).forEach((key) => {
    const bp = BREAKPOINTS[key];
    const config = styles[key] || {};
    
    let rules = "";
    
    // Only non-color properties are breakpoint-specific
    const ta = config.ta || config.textAlign;
    const va = config.va || config.verticalAlign;
    const fs = config.fs || config.fontSize;
    const fw = config.fw || config.fontWeight;
    
    if (ta) rules += `text-align:${ta}!important;`;
    if (va) rules += `vertical-align:${va}!important;`;
    if (fs) rules += `font-size:${fs}px!important;`;
    if (fw) rules += `font-weight:${fw}!important;`;
    
    // Handle compact padding (p: "8,12,8,12")
    const p = config.p as string | undefined;
    if (p && typeof p === 'string') {
      const [pt, pr, pb, pl] = p.split(',');
      rules += `padding:${pt}px ${pr}px ${pb}px ${pl}px!important;`;
    } else if (config.padding) {
      const pad = config.padding as { top?: string; right?: string; bottom?: string; left?: string };
      rules += `padding:${pad.top || 8}px ${pad.right || 12}px ${pad.bottom || 8}px ${pad.left || 12}px!important;`;
    }
    
    // Handle compact border (bd: "t:color:width;r:color:width;...")
    const bd = config.bd as string | undefined;
    if (bd && typeof bd === 'string') {
      rules += generateCompactBorderCSS(bd);
    } else if (config.border) {
      const b = config.border as { top?: { color?: string; width?: string }; right?: { color?: string; width?: string }; bottom?: { color?: string; width?: string }; left?: { color?: string; width?: string } };
      rules += generateFullBorderCSS(b);
    }
      
      // Only create media query if there are breakpoint-specific rules
      if (rules) {
        const mq = bp.max !== Infinity ? `@media (min-width: ${bp.min}px) and (max-width: ${bp.max}px)` : `@media (min-width: ${bp.min}px)`;
        const formattedRules = rules.replace(/;/g, ';\n        ').slice(0, -9); // Remove trailing newline+spaces
        css += `\n${mq} {\n    ${selector} {\n        ${formattedRules}\n    }\n}\n`;
      }
    });
  
  return css;
};

/**
 * Generate CSS for cell styles (legacy ID-based approach for backwards compatibility)
 */
export const generateCellCSS = (
  cellId: string,
  styles: Record<string, Record<string, unknown>>,
  tableId?: string
): string => {
  let css = "";
  const t = tableId ? `table[data-table-id="${tableId}"]` : "table";
  const selector = `${t} td[data-cell-id="${cellId}"],${t} th[data-cell-id="${cellId}"]`;
  
  // Get global colors from xs breakpoint (colors are stored only in xs since they're global)
  const xsConfig = styles.xs || {};
  const globalBg = xsConfig.bg || xsConfig.backgroundColor;
  const globalColor = xsConfig.c || xsConfig.textColor;
  
  // Output global colors ONCE without media queries
  let globalRules = "";
  if (globalBg) globalRules += `background-color:${globalBg}!important;`;
  if (globalColor) globalRules += `color:${globalColor}!important;`;
  if (globalRules) {
    css += `${selector}{${globalRules}}`;
  }
  
  // Only output breakpoint-specific properties in media queries
  (Object.keys(BREAKPOINTS) as BreakpointKey[]).forEach((key) => {
    const bp = BREAKPOINTS[key];
    const config = styles[key] || {};
    
    let rules = "";
    
    // Only non-color properties are breakpoint-specific
    const ta = config.ta || config.textAlign;
    const va = config.va || config.verticalAlign;
    const fs = config.fs || config.fontSize;
    const fw = config.fw || config.fontWeight;
    
    if (ta) rules += `text-align:${ta}!important;`;
    if (va) rules += `vertical-align:${va}!important;`;
    if (fs) rules += `font-size:${fs}px!important;`;
    if (fw) rules += `font-weight:${fw}!important;`;
    
    // Handle compact padding (p: "8,12,8,12")
    const p = config.p as string | undefined;
    if (p && typeof p === 'string') {
      const [pt, pr, pb, pl] = p.split(',');
      rules += `padding:${pt}px ${pr}px ${pb}px ${pl}px!important;`;
    } else if (config.padding) {
      const pad = config.padding as { top?: string; right?: string; bottom?: string; left?: string };
      rules += `padding:${pad.top || 8}px ${pad.right || 12}px ${pad.bottom || 8}px ${pad.left || 12}px!important;`;
    }
    
    // Handle compact border (bd: "t:color:width;r:color:width;...")
    const bd = config.bd as string | undefined;
    if (bd && typeof bd === 'string') {
      rules += generateCompactBorderCSS(bd);
    } else if (config.border) {
      const b = config.border as { top?: { color?: string; width?: string }; right?: { color?: string; width?: string }; bottom?: { color?: string; width?: string }; left?: { color?: string; width?: string } };
      rules += generateFullBorderCSS(b);
    }
    
    // Only create media query if there are breakpoint-specific rules
    if (rules) {
      const mq = bp.max !== Infinity ? `@media(min-width:${bp.min}px)and(max-width:${bp.max}px)` : `@media(min-width:${bp.min}px)`;
      css += `${mq}{${selector}{${rules}}}`;
    }
  });
  
  return css;
};

/**
 * Generate CSS for row styles using nth-child selector
 * (because TipTap doesn't preserve custom data attributes on tr elements)
 */
export const generateRowCSS = (
  rowIndex: number, 
  styles: Record<string, Partial<BreakpointRowConfig>>,
  tableId?: string
): string => {
  let rowCSS = "";
  // nth-child is 1-indexed
  const nthChild = rowIndex + 1;
  const tableSelector = tableId ? `table[data-table-id="${tableId}"]` : "table";
  const defaults = createDefaultRowConfig();
  
  (Object.keys(BREAKPOINTS) as BreakpointKey[]).forEach((key) => {
    const bp = BREAKPOINTS[key];
    const config = styles[key];
    if (!config) return;
    
    let rowRules = "";
    let cellRules = "";
    
    // Merge with defaults
    const heightType = config.heightType !== undefined ? config.heightType : defaults.heightType;
    const heightValue = config.heightValue !== undefined ? config.heightValue : defaults.heightValue;
    const backgroundColor = config.backgroundColor !== undefined ? config.backgroundColor : defaults.backgroundColor;
    const textColor = config.textColor !== undefined ? config.textColor : defaults.textColor;
    
    // Height (applies to tr)
    if (heightType !== "auto" && heightValue) {
      rowRules += `height: ${heightValue}px !important; `;
    }
    
    // Colors (applies to both tr and cells)
    if (backgroundColor) {
      rowRules += `background-color: ${backgroundColor} !important; `;
      cellRules += `background-color: ${backgroundColor} !important; `;
    }
    if (textColor) {
      rowRules += `color: ${textColor} !important; `;
      cellRules += `color: ${textColor} !important; `;
    }
    
    // Border styles (apply to cells in the row for better visibility)
    if (config.border) {
      cellRules += generateRowBorderCSS(config.border);
    }
    
    if (rowRules || cellRules) {
      rowCSS += `
@media (min-width: ${bp.min}px) ${bp.max !== Infinity ? `and (max-width: ${bp.max}px)` : ""} {
  ${rowRules ? `${tableSelector} tr:nth-child(${nthChild}) { ${rowRules} }` : ""}
  ${cellRules ? `${tableSelector} tr:nth-child(${nthChild}) td, ${tableSelector} tr:nth-child(${nthChild}) th { ${cellRules} }` : ""}
}`;
    }
  });
  
  return rowCSS;
};

